import { z } from 'zod';
import { dateField, phoneNumberField } from '@shared/schemas';
import { SafetyViolationTypes } from '../constants/safety.const';
import { SafetyEventStatuses, StatusChannels, NotificationStatuses } from '../constants/statuses.const';
import { minimizedProfessionSchema } from './IMinimizedProfession';
import { managerSchema } from './IManager';
import { tradeSchema } from './ITrade';
import { baseCertificateSchema } from './IBaseCertificate';
import { baseFloorSchema } from './IBaseFloor';
import { baseAreaSchema } from './IBaseArea';

// Inline schemas for nested types

const safetyViolationKeysSchema = z.object({
	tagId: z.string().nullable(),
	professionId: z.string().nullable(),
	floorId: z.string().nullable(),
	areaId: z.string().nullable(),
	activityId: z.string().nullable(),
	tradeId: z.string().nullable(),
	certificateName: z.string().nullable(),
});

const baseLocationSchema = baseFloorSchema.extend({
	shortFloorNick: z.string().optional(),
	anchorId: z.string().optional(),
	anchorNick: z.string().optional(),
	areas: z.array(baseAreaSchema),
});

const safetyBaseTagSchema = z.object({
	tagId: z.string(),
	tagNick: z.string(),
	tagOwner: z.string().optional(),
	phoneNumber: phoneNumberField,
});

const tagCertificateSchema = baseCertificateSchema.omit({ groupName: true, isCommonlyUsed: true }).extend({
	expirationDate: dateField,
	creationDate: dateField,
});

const smsNotificationSchema = z.object({
	manager: managerSchema,
	status: z.enum(NotificationStatuses),
	notificationSentDateTime: dateField.optional(),
});

const safetyEventActivitySchema = z.object({
	activityId: z.string(),
	activityDescription: z.string(),
});

// Schemas (source of truth) — pure validation, no transforms
export const safetyEventSchema = z.object({
	_id: z.string().optional(),
	keys: safetyViolationKeysSchema,
	status: z.enum(SafetyEventStatuses),
	statusChannel: z.enum(StatusChannels),
	safetyViolationType: z.enum(SafetyViolationTypes),
	projectId: z.string(),
	isCritical: z.boolean(),
	lastUpdatedTimestamp: z.number(),
	timestampCreated: z.number(),
	smsNotifications: z.array(smsNotificationSchema),
	profession: minimizedProfessionSchema.nullable(),
	manager: managerSchema.nullable(),
	timestampClosed: z.number().nullable(),
	location: baseLocationSchema.nullable(),
	tags: z.array(safetyBaseTagSchema).nullable(),
	activity: safetyEventActivitySchema.nullable(),
	trade: tradeSchema.nullable(),
	expiredCertificate: tagCertificateSchema.nullable(),
	linkedIssueId: z.string().optional(),
});

// Types (derived from schemas)
export type ISafetyEvent = z.infer<typeof safetyEventSchema>;
